import { StateCreator } from "zustand";
import { VendState } from ".";
import {
  Integration,
  IntegrationConfig,
  TelemetryMethodSyncUpdate,
  TelemetryProviderEntry,
} from "../types";
import {
  createTelemetryConnection,
  deleteTelemetryConnection,
  editTelemetryConnection,
  fetchTelemetryConnections,
  updateTelemetryMethodConfig,
} from "../api/telemetry/api";
import {
  normalizeTelemetryProvider,
  telemetryProviderCanCreateWithoutCredential,
  telemetryProviderRequiresSecret,
} from "../integrations/utils";
import { runLoad } from "./helpers";
import { withWriteAction } from "../utils";
import { getTaskIdentityKey } from "../taskContext";

export type IntegrationSlice = {
  integrationConfig: IntegrationConfig;
  integrations: Integration[];
  integrationsHydrated: boolean;
  updateIntegrationConfig: (updates: Partial<IntegrationConfig>) => void;
  loadIntegrations: (options?: { force?: boolean }) => Promise<void>;
  mergeTelemetryMethodSyncUpdates: (
    updates: TelemetryMethodSyncUpdate[]
  ) => void;
  saveIntegrations: (
    telemetry: IntegrationConfig["telemetryProviders"]
  ) => Promise<void>;
};

let integrationsPromise: Promise<void> | null = null;
let integrationsGeneration = 0;
export const resetIntegrationLoadState = () => {
  integrationsGeneration += 1;
  integrationsPromise = null;
};

const telemetryProviderFromIntegration = (
  integration: Integration
): TelemetryProviderEntry => ({
  id: integration.id,
  provider: integration.service_type || "",
  apiKey: integration.api_key,
  hasSecret: integration.has_secret,
  cardTransactionSurchargeCents:
    Number(integration.card_transaction_surcharge_cents || 0) || 0,
  methods: integration.methods || [],
});

export const createIntegrationSlice: StateCreator<
  VendState,
  [],
  [],
  IntegrationSlice
> = (set, get) => ({
  integrationConfig: {
    telemetryProviders: [],
    webhooksEnabled: false,
    webhookUrl: "",
    notificationsEmail: "",
  },
  integrations: [],
  integrationsHydrated: false,
  updateIntegrationConfig: (updates) =>
    set((state) => ({
      ...(state as VendState),
      integrationConfig: {
        ...(state as VendState).integrationConfig,
        ...updates,
      },
    })),
  mergeTelemetryMethodSyncUpdates: (updates) => {
    const normalizedUpdates = (updates || []).filter(
      (update) => update?.id && update?.telemetryId
    );
    if (!normalizedUpdates.length) return;
    set((state) => {
      let changed = false;
      const updateByTelemetryMethod = new Map(
        normalizedUpdates.map((update) => [
          `${update.telemetryId}:${update.id}`,
          update,
        ])
      );
      const integrations = (state as VendState).integrations.map((integration) => {
        if (!integration.id || !integration.methods?.length) return integration;
        let integrationChanged = false;
        const methods = integration.methods.map((method) => {
          const update = updateByTelemetryMethod.get(
            `${integration.id}:${method.id}`
          );
          if (!update) return method;
          integrationChanged = true;
          return {
            ...method,
            status: update.status,
            enabled: update.enabled,
            priority: update.priority,
            lastSyncAt: update.lastSyncAt ?? null,
            lastReceivedAt: update.lastReceivedAt ?? null,
          };
        });
        if (!integrationChanged) return integration;
        changed = true;
        return { ...integration, methods };
      });
      if (!changed) return state;
      const telemetryProviders = integrations.map(telemetryProviderFromIntegration);
      return {
        ...(state as VendState),
        integrations,
        integrationConfig: {
          ...(state as VendState).integrationConfig,
          telemetryProviders,
        },
      };
    });
  },
  loadIntegrations: async (options) => {
    if (integrationsPromise) return integrationsPromise;
    if ((get() as VendState).integrationsHydrated && !options?.force) return;
    const scope = getTaskIdentityKey();
    const generation = integrationsGeneration;
    const current = () => scope === getTaskIdentityKey() && generation === integrationsGeneration;
    const request = (async () => {
      try {
        await runLoad({
          fetcher: fetchTelemetryConnections,
          onSuccess: (integrations) => {
            if (!current()) return;
            // backend already returns camelCase
            const normalized = integrations.map((integration: Integration) => ({
              ...integration,
            }));
            // map backend integrations to telemetry providers
            const telemetryProviders = normalized.map(telemetryProviderFromIntegration);
            set({
              integrations: normalized,
              integrationConfig: {
                ...(get() as VendState).integrationConfig,
                telemetryProviders,
              },
            } as VendState);
          },
        });
      } finally {
        if (current()) set({ integrationsHydrated: true } as VendState);
        if (generation === integrationsGeneration) integrationsPromise = null;
      }
    })();
    integrationsPromise = request;
    return request;
  },
  saveIntegrations: async (telemetry) => {
    return withWriteAction("Saving Integrations", async () => {
      // persist new providers to backend and sync updates/removals for existing ones
      const state = get();
      const current = (telemetry || []).map((entry) => ({
        ...entry,
        provider: normalizeTelemetryProvider(entry.provider),
        apiKey: entry.apiKey?.trim(),
      }));
      const shouldRefreshMachines = current.some(
        (entry) => entry.applyCardTransactionSurchargeToMachines
      );
      const methodUpdates = current.flatMap((entry) =>
        (entry.methodConfigUpdates || []).map((update) => ({
          ...update,
          telemetryId: entry.id,
        }))
      );
      const byId = new Map(
        (state.integrations || [])
          .filter((i) => i.id)
          .map((i) => [i.id as string, i])
      );

      const currentIds = new Set(current.filter((entry) => entry.id).map((entry) => entry.id as string));
      const toDelete = (state.integrations || []).filter((entry) => entry.id && !currentIds.has(entry.id as string));
      const toUpdate = current.filter((entry) => {
        if (!entry.id || !entry.provider) return false;
        const existing = byId.get(entry.id);
        if (!existing) return true;
        const provider = entry.provider || "";
        const existingProvider = (existing.service_type || "").toLowerCase();
        if (provider !== existingProvider) return true;
        if (
          Number(entry.cardTransactionSurchargeCents || 0) !==
          Number(existing.card_transaction_surcharge_cents || 0)
        )
          return true;
        if (entry.applyCardTransactionSurchargeToMachines) return true;
        if (!telemetryProviderRequiresSecret(provider) && !entry.apiKey) return false;
        return (entry.apiKey || "") !== (existing.api_key || "");
      });

      const newOnes = current.filter(
        (entry) =>
          !entry?.id &&
          !!entry?.provider &&
          (telemetryProviderCanCreateWithoutCredential(entry.provider) ||
            !!entry?.apiKey)
      );
      const created: Integration[] = [];
      const updated: Integration[] = [];
      const deletedIds = new Set<string>();
      const writeErrors: unknown[] = [];

      for (const entry of toDelete) {
        try {
          await deleteTelemetryConnection(entry.id as string);
          deletedIds.add(entry.id as string);
        } catch (error) {
          console.error("Failed to delete integration", error);
          writeErrors.push(error);
        }
      }

      for (const entry of toUpdate) {
        const serviceType = entry.provider || "nayax";
        const existing = entry.id ? byId.get(entry.id) : undefined;
        const unchangedDisplayKey =
          !!entry.hasSecret &&
          !!entry.apiKey &&
          entry.apiKey === (existing?.api_key || "");
        try {
          const integration = await editTelemetryConnection(entry.id as string, {
            name: entry.provider || "Integration",
            apiKey: unchangedDisplayKey ? "" : entry.apiKey || "",
            serviceType,
            cardTransactionSurchargeCents: entry.cardTransactionSurchargeCents || 0,
            applyCardTransactionSurchargeToMachines:
              !!entry.applyCardTransactionSurchargeToMachines,
          });
          updated.push({
            ...integration,
            service_type: integration.service_type?.toLowerCase(),
          });
        } catch (error) {
          console.error("Failed to update integration", error);
          writeErrors.push(error);
        }
      }

      for (const entry of newOnes) {
        const serviceType = entry.provider || "nayax";
        try {
          const integration = await createTelemetryConnection({
            name: entry.provider || "Integration",
            apiKey: entry.apiKey || "",
            serviceType,
            cardTransactionSurchargeCents: entry.cardTransactionSurchargeCents || 0,
            applyCardTransactionSurchargeToMachines:
              !!entry.applyCardTransactionSurchargeToMachines,
          });
          created.push({
            ...integration,
            service_type: integration.service_type?.toLowerCase(),
          });
        } catch (error) {
          console.error("Failed to create integration", error);
          writeErrors.push(error);
        }
      }

      const methodUpdatesById = new Map<string, Awaited<ReturnType<typeof updateTelemetryMethodConfig>>>();
      for (const update of methodUpdates) {
        if (!update.id) continue;
        try {
          const updatedMethod = await updateTelemetryMethodConfig(update.id, {
            enabled: update.enabled,
            status: update.status,
            priority: update.priority,
            frequency: update.frequency,
            publicConfig: update.publicConfig,
            privateConfig: update.privateConfig,
          });
          methodUpdatesById.set(updatedMethod.id, updatedMethod);
        } catch (error) {
          console.error("Failed to update telemetry method", error);
          writeErrors.push(error);
        }
      }

      if (writeErrors.length) {
        const firstError = writeErrors[0];
        if (firstError instanceof Error) throw firstError;
        throw new Error("Failed to save integrations");
      }

      const updatedById = new Map(
        updated.filter((entry) => entry.id).map((entry) => [entry.id as string, entry])
      );
      const mergedTelemetry = current
        .filter((entry) => !entry.id || !deletedIds.has(entry.id))
        .map((entry) => {
          if (entry?.id) {
            const updatedMatch = updatedById.get(entry.id);
            return updatedMatch
              ? {
                  ...entry,
                  provider:
                    updatedMatch.service_type?.toLowerCase() ||
                    updatedMatch.service_type,
                  hasSecret: updatedMatch.has_secret,
                  cardTransactionSurchargeCents:
                    Number(updatedMatch.card_transaction_surcharge_cents || 0) || 0,
                  methods: (updatedMatch.methods || []).map(
                    (method) => methodUpdatesById.get(method.id) || method
                  ),
                  methodConfigUpdates: undefined,
                }
              : {
                  ...entry,
                  methods: (entry.methods || []).map(
                    (method) => methodUpdatesById.get(method.id) || method
                  ),
                  methodConfigUpdates: undefined,
                };
          }
          const createdMatch = created.shift();
          return createdMatch
            ? {
                id: createdMatch.id,
                provider:
                  createdMatch.service_type?.toLowerCase() ||
                  createdMatch.service_type,
                apiKey: entry?.apiKey,
                hasSecret: createdMatch.has_secret,
                cardTransactionSurchargeCents:
                  Number(createdMatch.card_transaction_surcharge_cents || 0) || 0,
                methods: createdMatch.methods || [],
                methodConfigUpdates: undefined,
              }
            : { ...entry, methodConfigUpdates: undefined };
        });
      set({
        integrations: [
          ...(state.integrations || []).filter(
            (i) => !i.id || !deletedIds.has(i.id)
          ),
          ...created,
          ...updated,
        ],
        integrationConfig: {
          ...state.integrationConfig,
          telemetryProviders: mergedTelemetry,
        },
      } as VendState);
      if (shouldRefreshMachines) {
        await (get() as VendState).loadMachines({ force: true });
      }
    });
  },
});
